import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHome, faPowerOff, faSyncAlt } from '@fortawesome/free-solid-svg-icons'
import { Breadcrumb, Button, Card, Col, Form, ProgressBar, Row, Spinner, Table } from '@themesberg/react-bootstrap'
import { toast } from 'react-toastify'
import { fetchServerBenchmark, restartForeverShell } from '../../../controller/api/serverBenchmarkApi'

const POLL_INTERVAL_MS = 5000

function formatBytes (n) {
  if (n == null || Number.isNaN(n)) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let v = n
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i += 1
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

function formatUptime (sec) {
  if (sec == null) return '—'
  const d = Math.floor(sec / 86400)
  const h = Math.floor((sec % 86400) / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const parts = []
  if (d) parts.push(`${d} ngày`)
  if (h || d) parts.push(`${h} giờ`)
  parts.push(`${m} phút`)
  parts.push(`${s} giây`)
  return parts.join(', ')
}

function latencyBadgeClass (ms) {
  if (ms == null) return 'secondary'
  if (ms < 20) return 'success'
  if (ms < 100) return 'warning'
  return 'danger'
}

export default function ServerBenchmark () {
  const [cookies] = useCookies(['lottmekong'])
  const token = cookies.lottmekong

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [restartingApi, setRestartingApi] = useState(false)
  const [restartingAll, setRestartingAll] = useState(false)
  const [restartShellPassword, setRestartShellPassword] = useState('')

  const loadRef = useRef(async () => {})

  const load = useCallback(async (opts = {}) => {
    const silent = opts.silent === true
    if (!token) return
    if (!silent) setLoading(true)
    try {
      const snap = await fetchServerBenchmark(token)
      setData(snap)
      setLastUpdated(new Date())
    } catch (err) {
      if (!silent) {
        const msg = err.response?.data?.message || err.message || 'Không tải được benchmark'
        toast.error(msg)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [token])

  loadRef.current = load

  useEffect(() => {
    document.title = 'Hiệu năng server'
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!token || !autoRefresh) return undefined

    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return
      await loadRef.current({ silent: true })
    }

    const id = setInterval(tick, POLL_INTERVAL_MS)
    const onVisible = () => {
      if (!document.hidden) tick()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [token, autoRefresh])

  const restartPwd = restartShellPassword.trim()
  const canRestartShell = Boolean(token && restartPwd)

  const handleRestartApi = useCallback(async () => {
    if (!token || !restartPwd) return
    if (!window.confirm('Khởi động lại tiến trình API (Forever prod)? Trang có thể mất kết nối vài giây. IP của bạn phải nằm trong whitelist admin.')) return
    setRestartingApi(true)
    try {
      await restartForeverShell(token, 'api', restartPwd)
      toast.success('Đã lên lịch khởi động lại API.')
      setRestartShellPassword('')
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Không gọi được restart API'
      toast.error(msg)
    } finally {
      setRestartingApi(false)
    }
  }, [token, restartPwd])

  const handleRestartAll = useCallback(async () => {
    if (!token || !restartPwd) return
    if (!window.confirm('Khởi động lại TẤT CẢ Forever (API + roll + email + notification + …)? Hệ thống sẽ gián đoạn ngắn. IP của bạn phải nằm trong whitelist admin.')) return
    setRestartingAll(true)
    try {
      await restartForeverShell(token, 'all', restartPwd)
      toast.success('Đã lên lịch khởi động lại toàn bộ worker Forever.')
      setRestartShellPassword('')
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Không gọi được restart toàn bộ'
      toast.error(msg)
    } finally {
      setRestartingAll(false)
    }
  }, [token, restartPwd])

  const mem = data?.process?.memoryBytes
  const heapPct = mem && mem.heapTotal > 0
    ? Math.min(100, Math.round((mem.heapUsed / mem.heapTotal) * 100))
    : null
  const hostMemPct = data?.host?.totalMemBytes > 0
    ? Math.round(((data.host.totalMemBytes - data.host.freeMemBytes) / data.host.totalMemBytes) * 100)
    : null

  const lat = data?.latencyMs || {}

  return (
    <>
      <div className="d-xl-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-xl-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: 'breadcrumb-dark breadcrumb-transparent' }}>
            <Breadcrumb.Item><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
            <Breadcrumb.Item>Cài đặt</Breadcrumb.Item>
            <Breadcrumb.Item active>Hiệu năng server</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <Card className="mb-4 border-0 shadow">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <Button variant="primary" size="sm" onClick={() => load()} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" className="me-1" /> : <FontAwesomeIcon icon={faSyncAlt} className="me-1" />}
              Làm mới ngay
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleRestartAll}
              disabled={!canRestartShell || restartingAll || restartingApi}
            >
              {restartingAll ? <Spinner animation="border" size="sm" className="me-1" /> : <FontAwesomeIcon icon={faPowerOff} className="me-1" />}
              Khởi động lại tất cả (Forever)
            </Button>
            <Form.Check
              type="switch"
              id="server-benchmark-auto"
              className="mb-0"
              label={`Tự động (${POLL_INTERVAL_MS / 1000}s)`}
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            {lastUpdated && (
              <span className="small text-muted">
                Cập nhật:{' '}
                <time dateTime={lastUpdated.toISOString()}>
                  {lastUpdated.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </time>
              </span>
            )}
          </div>

          <Row className="g-2 mb-3 align-items-end">
            <Col xs={12} md={4} lg={3}>
              <Form.Group className="mb-0">
                <Form.Label className="small mb-1">Mật khẩu restart server</Form.Label>
                <Form.Control
                  type="password"
                  autoComplete="off"
                  size="sm"
                  placeholder="ADMIN_RESTART_SHELL_PASSWORD"
                  value={restartShellPassword}
                  onChange={(e) => setRestartShellPassword(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={8} lg={9}>
              <p className="small text-muted mb-0">
                Cần mật khẩu trùng biến môi trường <code className="small">ADMIN_RESTART_SHELL_PASSWORD</code> trên backend và IP trình duyệt phải có trong <strong>AdminWhitelistIp</strong> (cùng cơ chế nạp tay).
              </p>
            </Col>
          </Row>

          <p className="small text-muted mb-4">
            <strong>Tiến trình Node (một PID)</strong> là tiến trình đang phục vụ HTTP (API). Các worker BullMQ (gửi mail, roll vé, tính thưởng) chạy <strong>process Node riêng</strong> — bảng dưới đọc heartbeat từ Redis (cùng máy, cùng Redis). Worker tắt hoặc hết hạn heartbeat (~45s) sẽ hiện <em>Offline</em>.
          </p>

          {!data && loading
            ? <Spinner animation="border" />
            : data && (
              <Row className="g-3">
                <Col md={6}>
                  <Card className="border h-100">
                    <Card.Body>
                      <Card.Title className="h6">API — tiến trình Node</Card.Title>
                      <p className="small text-muted mb-2">PID của process đang xử lý request admin (không phải worker BullMQ).</p>
                      <ul className="list-unstyled small mb-0">
                        <li><strong>PID:</strong> {data.process.pid}</li>
                        <li><strong>Node:</strong> {data.process.nodeVersion}</li>
                        <li><strong>Uptime:</strong> {formatUptime(data.process.uptimeSec)}</li>
                      </ul>
                      <hr />
                      <div className="small mb-1"><strong>Heap</strong> {formatBytes(mem?.heapUsed)} / {formatBytes(mem?.heapTotal)}</div>
                      {heapPct != null && (
                        <>
                          <ProgressBar className="progress-md mb-1" now={heapPct} min={0} max={100} />
                          <div className="small text-muted mb-2">{heapPct}% heap đang dùng</div>
                        </>
                      )}
                      <div className="small text-muted">
                        RSS {formatBytes(mem?.rss)} · external {formatBytes(mem?.external)}
                      </div>
                      <hr />
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={handleRestartApi}
                        disabled={!canRestartShell || restartingApi || restartingAll}
                      >
                        {restartingApi ? <Spinner animation="border" size="sm" className="me-1" /> : <FontAwesomeIcon icon={faPowerOff} className="me-1" />}
                        Khởi động lại API (Forever)
                      </Button>
                      <p className="small text-muted mt-2 mb-0">
                        Chạy script <code className="small">restart-api-forever.sh</code> trên server sau vài giây.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border h-100">
                    <Card.Body>
                      <Card.Title className="h6">Máy chủ (OS)</Card.Title>
                      <ul className="list-unstyled small mb-0">
                        <li><strong>Host:</strong> {data.host.hostname}</li>
                        <li><strong>Platform:</strong> {data.host.platform}</li>
                        <li><strong>CPU (logical):</strong> {data.host.cpuCount}</li>
                        <li><strong>Load avg:</strong> {data.host.loadAvg1m?.toFixed(2)} / {data.host.loadAvg5m?.toFixed(2)} / {data.host.loadAvg15m?.toFixed(2)}</li>
                      </ul>
                      <hr />
                      <div className="small mb-1">
                        <strong>RAM hệ thống</strong> {formatBytes(data.host.totalMemBytes - data.host.freeMemBytes)} / {formatBytes(data.host.totalMemBytes)} đã dùng
                      </div>
                      {hostMemPct != null && (
                        <>
                          <ProgressBar className="progress-md mb-1" variant="info" now={hostMemPct} min={0} max={100} />
                          <div className="small text-muted">{hostMemPct}% RAM máy</div>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={12}>
                  <Card className="border">
                    <Card.Body>
                      <Card.Title className="h6">Worker BullMQ (theo Redis)</Card.Title>
                      <Table responsive size="sm" className="mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Queue</th>
                            <th>Trạng thái</th>
                            <th>PID</th>
                            <th>Host</th>
                            <th>Uptime</th>
                            <th>Heap</th>
                            <th className="text-nowrap">Beat cuối</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.bullmqWorkers || []).map((w) => (
                            <tr key={w.queueKey}>
                              <td className="fw-bold">{w.queueKey}</td>
                              <td>
                                {w.online
                                  ? <span className="badge bg-success">Online</span>
                                  : <span className="badge bg-secondary">Offline</span>}
                              </td>
                              <td>{w.online && w.pid != null ? w.pid : '—'}</td>
                              <td className="small text-break">{w.online && w.hostname ? w.hostname : '—'}</td>
                              <td className="small">{w.online && w.uptimeSec != null ? formatUptime(w.uptimeSec) : '—'}</td>
                              <td className="small">{w.online && w.heapUsedBytes != null ? formatBytes(w.heapUsedBytes) : '—'}</td>
                              <td className="small text-nowrap">{w.online && w.lastBeatAt ? w.lastBeatAt : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={12}>
                  <Card className="border">
                    <Card.Body>
                      <Card.Title className="h6">Độ trễ kiểm tra</Card.Title>
                      <Row className="g-2">
                        <Col sm={6} md={4}>
                          <div className="border rounded p-3 text-center">
                            <div className="text-muted small">Redis PING</div>
                            <div className={`fs-4 text-${latencyBadgeClass(lat.redisPing)}`}>
                              {lat.redisPing != null ? `${lat.redisPing} ms` : 'Lỗi'}
                            </div>
                          </div>
                        </Col>
                        <Col sm={6} md={4}>
                          <div className="border rounded p-3 text-center">
                            <div className="text-muted small">Database SELECT 1</div>
                            <div className={`fs-4 text-${latencyBadgeClass(lat.databaseQuery)}`}>
                              {lat.databaseQuery != null ? `${lat.databaseQuery} ms` : 'Lỗi'}
                            </div>
                          </div>
                        </Col>
                        <Col sm={12} md={4}>
                          <div className="border rounded p-3 small text-muted">
                            <div><strong>Thu thập (ISO):</strong></div>
                            <div className="text-break">{data.collectedAt}</div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
        </Card.Body>
      </Card>
    </>
  )
}
